import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import {
  Heart, Activity, Wind, Gauge, Brain, Zap, ShieldCheck,
  Search, Calendar, ChevronDown, ChevronUp, Loader2,
  AlertTriangle, CheckCircle, Droplets, TrendingUp,
  Salad, FlaskConical, Dumbbell, Timer, User, Phone, X, RefreshCw, Camera,
  BarChart3, GitCompareArrows, QrCode, Monitor, ExternalLink
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import API from '../lib/api';

const FRONTEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

const VITAL_META = {
  heart_rate: { label: 'Heart Rate', unit: 'BPM', icon: Heart, ranges: [60, 100] },
  bp_systolic: { label: 'BP Systolic', unit: 'mmHg', icon: Activity, ranges: [90, 140] },
  bp_diastolic: { label: 'BP Diastolic', unit: 'mmHg', icon: Activity, ranges: [60, 90] },
  respiratory_rate: { label: 'Breathing', unit: '/min', icon: Wind, ranges: [12, 20] },
  spo2: { label: 'SpO2', unit: '%', icon: Droplets, ranges: [95, 100] },
  stress_index: { label: 'Stress', unit: '/100', icon: Brain, ranges: [0, 50] },
  hrv: { label: 'HRV', unit: 'ms', icon: Zap, ranges: [20, 80] },
  cardiac_output: { label: 'Cardiac Out', unit: 'L/min', icon: TrendingUp, ranges: [4, 8] },
};

function getStatus(key, val) {
  const m = VITAL_META[key];
  if (!m || val == null) return 'unknown';
  if (key === 'stress_index') return val <= 35 ? 'normal' : val <= 60 ? 'attention' : 'high';
  if (val >= m.ranges[0] && val <= m.ranges[1]) return 'normal';
  return 'attention';
}

export default function DoctorFaceVitalsPage() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [aiDialog, setAiDialog] = useState(null);
  const [screeningDialog, setScreeningDialog] = useState(null);
  const [stats, setStats] = useState(null);
  const [trendPatient, setTrendPatient] = useState(null);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [kioskDialogOpen, setKioskDialogOpen] = useState(false);
  const [clinicCode, setClinicCode] = useState('');
  const [loadingKioskCode, setLoadingKioskCode] = useState(false);

  const openKioskDialog = async () => {
    setKioskDialogOpen(true);
    if (clinicCode) return;
    setLoadingKioskCode(true);
    try {
      const res = await API.get('/kiosk/clinic-code');
      setClinicCode(res.data.clinic_code);
    } catch (e) {
      console.error(e);
    }
    setLoadingKioskCode(false);
  };

  const fetchScans = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [scansRes, statsRes] = await Promise.all([
        API.get('/face-vitals/doctor-scans', { params }),
        API.get('/face-vitals/doctor-stats')
      ]);
      setScans(scansRes.data.scans || []);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [search, dateFrom, dateTo]);

  useEffect(() => {
    const timer = setTimeout(fetchScans, 400);
    return () => clearTimeout(timer);
  }, [fetchScans]);

  const vitalKeys = ['heart_rate', 'bp_systolic', 'bp_diastolic', 'spo2', 'respiratory_rate', 'stress_index', 'hrv', 'cardiac_output'];

  // Unique patients for trend dropdown
  const uniquePatients = useMemo(() => {
    const map = {};
    scans.forEach(s => {
      const key = s.patient_phone || s.patient_name || s.id;
      if (!map[key]) map[key] = { phone: s.patient_phone, name: s.patient_name, count: 0 };
      map[key].count++;
    });
    return Object.values(map).filter(p => p.count >= 2);
  }, [scans]);

  // Trend data for selected patient
  const trendData = useMemo(() => {
    if (!trendPatient) return [];
    return [...scans].filter(s => s.patient_phone === trendPatient).reverse().map(s => {
      const d = new Date(s.created_at);
      const v = s.vitals || {};
      return {
        date: `${d.getDate()}/${d.getMonth() + 1}`,
        heart_rate: v.heart_rate || null,
        spo2: v.spo2 || null,
        stress: v.stress_index || null,
        skin_score: s.face_screening?.overall_skin_health_score || null,
      };
    });
  }, [scans, trendPatient]);

  const toggleCompare = (scan) => {
    if (compareA?.id === scan.id) { setCompareA(null); return; }
    if (compareB?.id === scan.id) { setCompareB(null); return; }
    if (!compareA) { setCompareA(scan); return; }
    if (!compareB) { setCompareB(scan); setShowCompare(true); return; }
    setCompareA(scan); setCompareB(null);
  };

  return (
    <div className="space-y-6" data-testid="doctor-face-vitals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Face Vitals Scans</h1>
          <p className="text-sm text-slate-500">Patient wellness screening results & AI insights</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchScans} data-testid="fv-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Launch Waiting Room Kiosk - primary CTA */}
      <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50">
        <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-teal-600 flex items-center justify-center shrink-0">
              <Monitor className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-sm text-slate-800" style={{ fontFamily: 'Manrope' }}>Waiting Room Kiosk</p>
              <p className="text-xs text-slate-500">Let arriving patients self-screen vitals via camera or QR code, routed straight to this queue</p>
            </div>
          </div>
          <Button
            data-testid="launch-kiosk-btn"
            onClick={openKioskDialog}
            className="bg-teal-700 hover:bg-teal-800 text-white h-11 shrink-0 w-full sm:w-auto"
          >
            <Monitor className="w-4 h-4 mr-2" /> Launch Waiting Room Kiosk
          </Button>
        </CardContent>
      </Card>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-teal-700">{stats.total_scans}</p>
              <p className="text-xs text-slate-500">Total Scans</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-teal-700">{stats.today_scans}</p>
              <p className="text-xs text-slate-500">Today</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.attention_cases?.length || 0}</p>
              <p className="text-xs text-slate-500">Need Attention</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{(stats.total_scans || 0) - (stats.attention_cases?.length || 0)}</p>
              <p className="text-xs text-slate-500">Normal</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="fv-search-input"
                placeholder="Search by patient name, phone, or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <div className="flex gap-2 items-center">
              <Calendar className="w-4 h-4 text-slate-400 hidden sm:block flex-shrink-0" />
              <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto items-center">
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-full sm:w-36 text-sm" data-testid="fv-date-from" />
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-full sm:w-36 text-sm" data-testid="fv-date-to" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patient Trends */}
      {uniquePatients.length > 0 && (
        <Card className="border-slate-200" data-testid="doctor-trends-card">
          <CardContent className="p-3 space-y-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-bold text-slate-700" style={{ fontFamily: 'Manrope' }}>Patient Trends</span>
              <select
                data-testid="trend-patient-select"
                value={trendPatient || ''}
                onChange={e => setTrendPatient(e.target.value || null)}
                className="ml-auto h-8 rounded-md border border-input bg-background px-2 text-xs"
              >
                <option value="">Select patient...</option>
                {uniquePatients.map(p => (
                  <option key={p.phone} value={p.phone}>{p.name || p.phone} ({p.count} scans)</option>
                ))}
              </select>
            </div>
            {trendData.length >= 2 && (
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-500 mb-1 font-medium">Vitals Over Time</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Line type="monotone" dataKey="heart_rate" name="HR" stroke="#f43f5e" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                      <Line type="monotone" dataKey="spo2" name="SpO2" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                      <Line type="monotone" dataKey="stress" name="Stress" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {trendData.some(d => d.skin_score != null) && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1 font-medium">Face Screening Score</p>
                    <ResponsiveContainer width="100%" height={140}>
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#94a3b8" />
                        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                        <Line type="monotone" dataKey="skin_score" name="Skin Health" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
            {trendPatient && trendData.length < 2 && (
              <p className="text-xs text-slate-400 text-center py-2">Need at least 2 scans to show trends</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Compare toolbar */}
      {(compareA || compareB) && (
        <div className="flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg text-xs" data-testid="compare-toolbar">
          <GitCompareArrows className="w-4 h-4 text-indigo-600" />
          <span className="text-indigo-700 font-medium">Compare:</span>
          <span className="text-indigo-600">{compareA ? `${compareA.patient_name} (${new Date(compareA.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'})})` : 'Select 1st'}</span>
          <span className="text-indigo-400">vs</span>
          <span className="text-indigo-600">{compareB ? `${compareB.patient_name} (${new Date(compareB.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'})})` : 'Select 2nd'}</span>
          {compareA && compareB && (
            <Button size="sm" className="ml-auto h-6 text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setShowCompare(true)} data-testid="open-compare-btn">View</Button>
          )}
          <Button size="sm" variant="ghost" className="h-6 text-[10px] text-indigo-600" onClick={() => { setCompareA(null); setCompareB(null); }}>Clear</Button>
        </div>
      )}

      {/* Scans list */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Loading scans...</p>
        </div>
      ) : scans.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Heart className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No face vitals scans found</p>
          {search && <p className="text-xs mt-1">Try a different search term</p>}
        </div>
      ) : (
        <div className="space-y-3" data-testid="fv-scans-list">
          {scans.map(scan => {
            const v = scan.vitals || {};
            const isExpanded = expandedId === scan.id;
            const hasAttention = vitalKeys.some(k => getStatus(k, v[k]) !== 'normal');
            const scanDate = new Date(scan.created_at || new Date().toISOString());

            return (
              <Card key={scan.id} className={`border ${hasAttention ? 'border-amber-200' : 'border-slate-200'} hover:shadow-sm transition-shadow`} data-testid={`fv-scan-${scan.id}`}>
                <CardContent className="p-0">
                  {/* Summary row */}
                  <button
                    className="w-full p-4 flex items-center justify-between text-left"
                    onClick={() => setExpandedId(isExpanded ? null : scan.id)}
                    data-testid={`fv-scan-toggle-${scan.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasAttention ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                        {hasAttention ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <CheckCircle className="w-5 h-5 text-emerald-600" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm text-slate-800">{scan.patient_name || 'Unknown Patient'}</p>
                          <Badge variant="outline" className="text-[10px]">{scan.source === 'kiosk_checkin' ? 'Kiosk' : 'Self'}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          {scan.patient_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{scan.patient_phone}</span>}
                          <span>{scanDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} {scanDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="hidden sm:flex items-center gap-4 text-sm">
                        <span className="text-slate-700"><Heart className="w-3.5 h-3.5 inline mr-1 text-rose-400" />{v.heart_rate || '--'}</span>
                        <span className="text-slate-700"><Activity className="w-3.5 h-3.5 inline mr-1 text-red-400" />{v.bp_systolic || '--'}/{v.bp_diastolic || '--'}</span>
                        <span className="text-slate-700"><Droplets className="w-3.5 h-3.5 inline mr-1 text-blue-400" />{v.spo2 || '--'}%</span>
                      </div>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
                      {/* Vitals grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {vitalKeys.map(key => {
                          const meta = VITAL_META[key];
                          const val = v[key];
                          const status = getStatus(key, val);
                          const Icon = meta.icon;
                          return (
                            <div key={key} className={`rounded-lg p-2 border ${status === 'normal' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <Icon className="w-3.5 h-3.5 opacity-60" />
                                <span className="text-[10px] font-medium opacity-70">{meta.label}</span>
                              </div>
                              <p className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>
                                {key === 'bp_systolic' ? `${v.bp_systolic}/${v.bp_diastolic}` : val ?? '--'}
                                <span className="text-[10px] font-normal ml-0.5 opacity-50">{key === 'bp_systolic' ? '' : meta.unit}</span>
                              </p>
                            </div>
                          );
                        })}
                      </div>

                      {/* Extra info */}
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                        <span>Pulse: {v.pulse_regularity || '--'}</span>
                        <span>Vascular Age: {v.vascular_age || '--'} yrs</span>
                        <span>Signal: {v.signal_quality || '--'}%</span>
                        {scan.age && <span>Age: {scan.age}</span>}
                        {scan.gender && <span>Gender: {scan.gender}</span>}
                      </div>

                      {/* AI Insights button */}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          data-testid={`fv-ai-btn-${scan.id}`}
                          size="sm"
                          onClick={() => setAiDialog(scan)}
                          className="bg-teal-700 hover:bg-teal-800 text-white"
                        >
                          <Brain className="w-4 h-4 mr-1.5" /> AI Health Insights
                        </Button>
                        {(scan.face_screening && !scan.face_screening.error) && (
                          <Button
                            data-testid={`fv-screening-btn-${scan.id}`}
                            size="sm"
                            onClick={() => setScreeningDialog(scan)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            <Camera className="w-4 h-4 mr-1.5" /> Face Screening
                          </Button>
                        )}
                        <Button
                          data-testid={`fv-compare-btn-${scan.id}`}
                          size="sm"
                          variant={compareA?.id === scan.id || compareB?.id === scan.id ? 'default' : 'outline'}
                          onClick={() => toggleCompare(scan)}
                          className={compareA?.id === scan.id || compareB?.id === scan.id ? 'bg-indigo-600 text-white' : ''}
                        >
                          <GitCompareArrows className="w-4 h-4 mr-1.5" />
                          {compareA?.id === scan.id || compareB?.id === scan.id ? 'Selected' : 'Compare'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* AI Insights Dialog */}
      <Dialog open={!!aiDialog} onOpenChange={() => setAiDialog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Brain className="w-5 h-5 text-teal-600" /> AI Health Insights
              {aiDialog?.patient_name && <span className="text-sm font-normal text-slate-500">- {aiDialog.patient_name}</span>}
            </DialogTitle>
          </DialogHeader>
          {aiDialog && <AIInsightsContent scan={aiDialog} />}
        </DialogContent>
      </Dialog>

      {/* Face Screening Dialog */}
      <Dialog open={!!screeningDialog} onOpenChange={() => setScreeningDialog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Camera className="w-5 h-5 text-indigo-600" /> 1Clin AI Face Screening
              {screeningDialog?.patient_name && <span className="text-sm font-normal text-slate-500">- {screeningDialog.patient_name}</span>}
            </DialogTitle>
          </DialogHeader>
          {screeningDialog?.face_screening && <FaceScreeningContent screening={screeningDialog.face_screening} />}
        </DialogContent>
      </Dialog>

      {/* Scan Comparison Dialog */}
      <Dialog open={showCompare && !!compareA && !!compareB} onOpenChange={v => { if (!v) setShowCompare(false); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <GitCompareArrows className="w-5 h-5 text-indigo-600" /> Scan Comparison
            </DialogTitle>
          </DialogHeader>
          {compareA && compareB && <CompareContent a={compareA} b={compareB} vitalKeys={vitalKeys} />}
        </DialogContent>
      </Dialog>

      {/* Waiting Room Kiosk Launch Dialog */}
      <Dialog open={kioskDialogOpen} onOpenChange={setKioskDialogOpen}>
        <DialogContent className="max-w-md" data-testid="kiosk-launch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Monitor className="w-5 h-5 text-teal-600" /> Waiting Room Kiosk
            </DialogTitle>
            <DialogDescription>
              Hand a tablet to the patient (tap "Open Kiosk Screen") or let them scan the QR below on their own phone. Vitals captured will appear in this queue automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="bg-white p-4 rounded-xl border border-slate-200">
              {loadingKioskCode || !clinicCode ? (
                <div className="w-[160px] h-[160px] flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <QRCodeSVG value={`${FRONTEND_URL}/checkin/${clinicCode}`} size={160} level="H" includeMargin={true} data-testid="kiosk-qr-code" />
              )}
            </div>
            {clinicCode && <p className="text-xs text-slate-400">Clinic Code: <span className="font-bold text-teal-700" data-testid="kiosk-clinic-code">{clinicCode}</span></p>}
            <Button
              className="w-full bg-teal-700 hover:bg-teal-800 text-white h-11"
              disabled={!clinicCode}
              onClick={() => window.open(`${FRONTEND_URL}/checkin/${clinicCode}`, '_blank')}
              data-testid="open-kiosk-screen-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" /> Open Kiosk Screen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AIInsightsContent({ scan }) {
  const [ai, setAi] = useState(scan.ai_analysis || null);
  const [loading, setLoading] = useState(!scan.ai_analysis);

  useEffect(() => {
    if (scan.ai_analysis) { setAi(scan.ai_analysis); setLoading(false); return; }
    let alive = true;
    const poll = async () => {
      for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 2000));
        if (!alive) return;
        try {
          const res = await API.get(`/face-vitals/scan/${scan.id}`);
          if (res.data.ai_analysis && !res.data.ai_analysis.error) {
            setAi(res.data.ai_analysis); setLoading(false); return;
          }
        } catch {}
      }
      setLoading(false);
    };
    poll();
    return () => { alive = false; };
  }, [scan]);

  if (loading) return (
    <div className="text-center py-10">
      <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-3" />
      <p className="text-sm text-slate-500">Generating AI analysis...</p>
    </div>
  );

  if (!ai) return (
    <div className="text-center py-10 text-slate-400">
      <Brain className="w-8 h-8 mx-auto mb-2 opacity-30" />
      <p className="text-sm">AI analysis not available</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className={`rounded-xl p-4 border ${ai.overall_status === 'Normal' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <p className="font-bold text-sm">{ai.overall_status}</p>
        <p className="text-sm text-slate-600 mt-1">{ai.summary}</p>
      </div>

      {ai.recommended_tests?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><FlaskConical className="w-4 h-4 text-violet-500" /> Recommended Tests</p>
          <div className="space-y-2">
            {ai.recommended_tests.map((t, i) => (
              <div key={`el-${i}`} className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-violet-800">{t.test_name}</p>
                  <Badge variant="outline" className={`text-[10px] ${t.urgency === 'urgent' ? 'border-rose-300 text-rose-600' : 'border-amber-300 text-amber-600'}`}>{t.urgency}</Badge>
                </div>
                <p className="text-xs text-violet-600 mt-0.5">{t.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {ai.diet_plan && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><Salad className="w-4 h-4 text-emerald-500" /> Diet Plan</p>
          <div className="space-y-2 text-sm">
            {ai.diet_plan.foods_to_include?.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <p className="font-medium text-emerald-700 text-xs mb-1">Include</p>
                <ul className="text-xs text-emerald-600 space-y-0.5 list-disc pl-4">
                  {ai.diet_plan.foods_to_include.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                </ul>
              </div>
            )}
            {ai.diet_plan.foods_to_avoid?.length > 0 && (
              <div className="bg-rose-50 border border-rose-100 rounded-lg p-3">
                <p className="font-medium text-rose-700 text-xs mb-1">Avoid</p>
                <ul className="text-xs text-rose-600 space-y-0.5 list-disc pl-4">
                  {ai.diet_plan.foods_to_avoid.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                </ul>
              </div>
            )}
            {ai.diet_plan.meal_suggestions?.length > 0 && (
              <div className="bg-sky-50 border border-sky-100 rounded-lg p-3">
                <p className="font-medium text-sky-700 text-xs mb-1">Meal Ideas</p>
                <ul className="text-xs text-sky-600 space-y-0.5 list-disc pl-4">
                  {ai.diet_plan.meal_suggestions.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                </ul>
              </div>
            )}
            {ai.diet_plan.hydration && (
              <p className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-lg p-2"><Droplets className="w-3 h-3 inline mr-1" />{ai.diet_plan.hydration}</p>
            )}
          </div>
        </div>
      )}

      {ai.lifestyle_recommendations?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><Dumbbell className="w-4 h-4 text-amber-500" /> Lifestyle Tips</p>
          <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5">
            {ai.lifestyle_recommendations.map((l, i) => <li key={`${l}-${i}`}>{l}</li>)}
          </ul>
        </div>
      )}

      {ai.follow_up && (
        <div className="bg-teal-50 border border-teal-100 rounded-lg p-3">
          <p className="text-xs text-teal-700"><Timer className="w-3 h-3 inline mr-1" /><b>Follow-up:</b> {ai.follow_up}</p>
        </div>
      )}
    </div>
  );
}


function FaceScreeningContent({ screening }) {
  const getSeverityColor = (severity) => {
    if (!severity) return 'slate';
    const s = severity.toLowerCase();
    if (s === 'none' || s === 'well-hydrated' || s === 'excellent') return 'emerald';
    if (s === 'mild' || s === 'mildly-dehydrated' || s === 'good') return 'amber';
    if (s === 'moderate' || s === 'dehydrated' || s === 'fair') return 'orange';
    if (s === 'severe' || s === 'poor') return 'rose';
    return 'slate';
  };
  const colorClasses = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-600',
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1 rounded-xl p-4 border bg-indigo-50 border-indigo-200 text-center">
          <p className="text-3xl font-bold text-indigo-700" style={{ fontFamily: 'Manrope' }}>{screening.overall_skin_health_score ?? '--'}</p>
          <p className="text-xs text-indigo-500 mt-1">Skin Health Score</p>
        </div>
        <div className="flex-1 rounded-xl p-4 border bg-violet-50 border-violet-200 text-center">
          <p className="text-3xl font-bold text-violet-700" style={{ fontFamily: 'Manrope' }}>{screening.ai_confidence ?? '--'}%</p>
          <p className="text-xs text-violet-500 mt-1">AI Confidence</p>
        </div>
      </div>
      <div className="rounded-xl p-4 border bg-slate-50 border-slate-200">
        <p className="text-sm text-slate-700">{screening.summary}</p>
      </div>
      {screening.screenings?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2">Screening Results</p>
          <div className="space-y-2">
            {screening.screenings.map((s, i) => {
              const c = getSeverityColor(s.severity);
              return (
                <div key={i} className={`rounded-lg p-3 border ${colorClasses[c]}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold">{s.name}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] capitalize">{s.severity}</Badge>
                      <span className="text-xs font-bold opacity-70">{s.score}/100</span>
                    </div>
                  </div>
                  <p className="text-xs opacity-80">{s.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {screening.recommendations?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2">Recommendations</p>
          <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5">
            {screening.recommendations.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {screening.disclaimer && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
          <p className="text-[11px] text-amber-700 flex gap-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{screening.disclaimer}</p>
        </div>
      )}
    </div>
  );
}


const COMPARE_META = {
  heart_rate: { label: 'Heart Rate', unit: 'BPM', ranges: [60, 100] },
  bp_systolic: { label: 'BP Systolic', unit: 'mmHg', ranges: [90, 140] },
  bp_diastolic: { label: 'BP Diastolic', unit: 'mmHg', ranges: [60, 90] },
  spo2: { label: 'SpO2', unit: '%', ranges: [95, 100] },
  respiratory_rate: { label: 'Breathing', unit: '/min', ranges: [12, 20] },
  stress_index: { label: 'Stress', unit: '/100', ranges: [0, 50] },
  hrv: { label: 'HRV', unit: 'ms', ranges: [20, 80] },
  cardiac_output: { label: 'Cardiac Out', unit: 'L/min', ranges: [4, 8] },
};

function CompareContent({ a, b, vitalKeys }) {
  const dateA = new Date(a.created_at);
  const dateB = new Date(b.created_at);
  const fmtDate = d => `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
  const va = a.vitals || {};
  const vb = b.vitals || {};

  const getDiff = (key) => {
    const valA = va[key], valB = vb[key];
    if (valA == null || valB == null) return null;
    return valB - valA;
  };

  return (
    <div className="space-y-4" data-testid="compare-content">
      {/* Headers */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div />
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
          <p className="text-xs font-bold text-slate-700">{a.patient_name || 'Scan A'}</p>
          <p className="text-[10px] text-slate-500">{fmtDate(dateA)}</p>
          <Badge variant="outline" className="text-[9px] mt-1">{a.source === 'kiosk_checkin' ? 'Kiosk' : 'Self'}</Badge>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
          <p className="text-xs font-bold text-slate-700">{b.patient_name || 'Scan B'}</p>
          <p className="text-[10px] text-slate-500">{fmtDate(dateB)}</p>
          <Badge variant="outline" className="text-[9px] mt-1">{b.source === 'kiosk_checkin' ? 'Kiosk' : 'Self'}</Badge>
        </div>
      </div>

      {/* Vitals comparison rows */}
      <div className="space-y-1">
        {vitalKeys.map(key => {
          const meta = COMPARE_META[key];
          const valA = va[key];
          const valB = vb[key];
          const diff = getDiff(key);
          const improved = key === 'stress_index' ? (diff != null && diff < 0) : (diff != null && diff >= 0);
          return (
            <div key={key} className="grid grid-cols-3 gap-2 items-center py-1.5 border-b border-slate-100 last:border-0">
              <p className="text-xs font-medium text-slate-600">{meta.label} <span className="text-[10px] text-slate-400">({meta.unit})</span></p>
              <p className="text-center text-sm font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>{valA ?? '--'}</p>
              <div className="text-center">
                <span className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>{valB ?? '--'}</span>
                {diff != null && diff !== 0 && (
                  <span className={`ml-1.5 text-[10px] font-semibold ${improved ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {diff > 0 ? '+' : ''}{key === 'cardiac_output' ? diff.toFixed(1) : Math.round(diff)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Face screening comparison */}
      {(a.face_screening || b.face_screening) && (
        <div>
          <p className="text-xs font-bold text-slate-600 mb-2">Face Screening Score</p>
          <div className="grid grid-cols-3 gap-2 items-center">
            <p className="text-xs text-slate-500">Skin Health</p>
            <p className="text-center text-lg font-bold text-indigo-700" style={{ fontFamily: 'Manrope' }}>{a.face_screening?.overall_skin_health_score ?? '--'}</p>
            <p className="text-center text-lg font-bold text-indigo-700" style={{ fontFamily: 'Manrope' }}>{b.face_screening?.overall_skin_health_score ?? '--'}</p>
          </div>
        </div>
      )}
    </div>
  );
}
